import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { Icon } from "@fluentui/react";
import { slot } from "@fluentui/react-components";

import { AppIcon, iconNameToIconProps } from "@1js/acui-icons";

import type { AppButtonState } from "../components/AppButton/next";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

/**
 * Minimum properties associated with setting the FontIcon.
 *
 * Original `icon` property in the Fabric-based controls in app-commanding-ui was a string property. That has been
 * replaced with `iconName`. The Fluent UI-based controls have an `icon` state property that is a shorthand property.
 */
export type StateWithIconAsStringOrShorthand = {
  icon?: AppButtonState["icon"];
  iconName?: string;
};

/**
 * Process the `state` as a mutable object, converting a Fabric-based string `icon` property (if it exists) to a
 * Fluent UI-based shorthand `icon` property.
 */
export function useShorthandFontIcon(
  state: StateWithIconAsStringOrShorthand,
  iconColor?: string,
  flipInRtl?: boolean,
  hideIcon?: boolean
) {
  const context = React.useContext(AppContext);
  const isV9IconEnabled = !!checkExperiment(
    context,
    ribbonExperiments.fluentV9IconIsEnabled
  );

  if (typeof state.icon?.children === "string") {
    const {
      icon: { children: iconAsString },
      iconName,
    } = state;
    const iconNameResolved = iconName ?? (iconAsString as string);

    state.icon = slot.always(
      {
        children: isV9IconEnabled ? (
          <AppIcon
            iconName={iconNameResolved}
            iconColor={iconColor}
            hideIcon={hideIcon}
            flipInRtl={flipInRtl}
          />
        ) : (
          <Icon
            {...iconNameToIconProps(
              iconNameResolved,
              iconColor,
              hideIcon,
              flipInRtl
            )}
          />
        ),
      },
      { elementType: "span" }
    );
  }
}
